import { useAppContext } from '@/contexts/AppContext';
import { Link, useLocation } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { community, tenantClientUrl } = useAppContext();

  const navLinks = useMemo(
    () => [
      { name: 'Home', path: '/' },
      { name: 'Available Floor Plans', path: '/floor-plans' },
      { name: 'Gallery', path: '/gallery' },
      {
        name: 'Residents',
        path: tenantClientUrl ? `${tenantClientUrl}/user/login` : '',
        isExternal: true,
      },
    ],
    [tenantClientUrl],
  );

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="bg-primary-600 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center">
            <img
              src={community.logo?.thumbnailUrl}
              alt={community.name || 'Community Logo'}
              className="w-full h-12 object-cover hover:scale-110 transition-transform duration-500"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex space-x-8">
            {navLinks.map((link) =>
              link.isExternal ? (
                <a
                  key={link.path}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-primary-200 hover:text-white transition-colors duration-200 uppercase tracking-wider text-sm"
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-medium transition-colors duration-200 uppercase tracking-wider text-sm ${
                    isActive(link.path)
                      ? 'text-white border-b-2 border-accent-500'
                      : 'text-primary-200 hover:text-white'
                  }`}
                >
                  {link.name}
                </Link>
              ),
            )}
          </div>

          {/* CTA Button (Desktop) */}
          <div className="hidden md:block">
            <Link
              to="/floor-plans"
              className="px-6 py-2 bg-accent-600 text-white rounded-none font-medium uppercase tracking-wider text-sm hover:bg-accent-700 transition-colors duration-200"
            >
              View Floor Plans
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-none text-primary-200 hover:text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-accent-500"
            aria-label="Toggle menu"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isOpen ? <path d="M6 18L18 6M6 6l12 12" /> : <path d="M4 6h16M4 12h16M4 18h16" />}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-primary-700 border-t border-primary-500">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navLinks.map((link) =>
              link.isExternal ? (
                <a
                  key={link.path}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsOpen(false)}
                  className="block px-3 py-2 rounded-none text-base font-medium text-primary-200 hover:text-white hover:bg-primary-600 transition-colors duration-200"
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`block px-3 py-2 rounded-none text-base font-medium transition-colors duration-200 ${
                    isActive(link.path)
                      ? 'bg-primary-800 text-white'
                      : 'text-primary-200 hover:text-white hover:bg-primary-600'
                  }`}
                >
                  {link.name}
                </Link>
              ),
            )}
            <Link
              to="/floor-plans"
              onClick={() => setIsOpen(false)}
              className="block w-full text-center mt-4 px-6 py-3 bg-accent-600 text-white rounded-none font-medium uppercase tracking-wider text-sm hover:bg-accent-700 transition-colors duration-200"
            >
              View Floor Plans
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
